import React, { useState, useEffect } from 'react';
import { Button, Form, Row, Col, Container, Card, CardBody } from 'reactstrap';
import { useSelector } from 'react-redux';
import TextField from '../../../common/TextField';
import { RootState } from '../../../../ReduxToolkit/Store';
import { useMutationRequestQuery } from '../../../../Hooks/useMutationRequestQuery';
import { toast } from 'react-toastify';
import MobileField from '../../../common/MobileField';
import { useQueryClient } from '@tanstack/react-query';
import { updateMMPClientDetails } from '../../../../Services/Auth';

export default function Profile() {
    const queryClient = useQueryClient()
    const [profileFormData, setProfileFormData] = useState({
        firstName: '',
        surname: '',
        email: '',
        mobile: ''
    });

    const [profileFormErrors, setProfileFormErrors] = useState({
        firstName: false,
        surname: false,
        email: false,
        mobile: false
    });

    const profileData = useSelector((state: RootState) => state.profile);
    const userData = useSelector((state: RootState) => state.auth);

    useEffect(() => {
        if (profileData) {
            setProfileFormData({
                firstName: profileData.firstName || '',
                surname: profileData.lastName || '',
                email: profileData.email || '',
                mobile: profileData.mobile || ''
            });
        }
    }, [profileData]);

    const {
        mutate: updateClientDetails,
        isPending: isUpdateClientDetailsPending,
    } = useMutationRequestQuery(
        'updateMMPClientDetails',
        updateMMPClientDetails,
        {
            onSuccess: () => {
                toast.success("Profile updated successfully.");
                queryClient.invalidateQueries({ queryKey: ['getMMPClientDetails'] });
            },
            onError: () => {
                toast.error("Failed to update profile.");
            },
        }
    );

    const handleProfileValueChange = (name: string, value: string) => {
        setProfileFormData(prevData => ({
            ...prevData,
            [name]: value,
        }));
    };

    const handleProfileErrorChange = (name: string, error: boolean) => {
        setProfileFormErrors(prevErrors => ({
            ...prevErrors,
            [name]: error,
        }));
    };

    // Validation Functions
    const validateStringField = (value: string) => value.trim() !== '';
    const validateEmailField = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        const firstNameValid = validateStringField(profileFormData.firstName);
        const surnameValid = validateStringField(profileFormData.surname);
        const emailValid = validateEmailField(profileFormData.email);
        const mobileValid = validateStringField(profileFormData.mobile);

        const isValid = firstNameValid && surnameValid && emailValid && mobileValid;

        setProfileFormErrors({
            firstName: !firstNameValid,
            surname: !surnameValid,
            email: !emailValid,
            mobile: !mobileValid
        });

        if (!isValid) {
            return;
        }
        const updatedData = {
            clientId: userData.nameidentifier,
            client1FirstName: profileFormData.firstName,
            client1LastName: profileFormData.surname,
            client1Email: profileFormData.email,
            client1Mobile: profileFormData.mobile,
        };
        updateClientDetails(updatedData);
    };
    const renderProfileForm = () => (
        <Form onSubmit={handleSubmit}>
            <Row>
                <TextField
                    label="First Name"
                    type="text"
                    name="firstName"
                    value={profileFormData.firstName}
                    setValue={handleProfileValueChange}
                    placeholder="First Name"
                    errorMessage="First name is required"
                    error={profileFormErrors.firstName}
                    passCol="col-8"
                    design="flex"
                    maxChars={20}
                    setError={handleProfileErrorChange}
                />
                <TextField
                    label="Surname"
                    type="text"
                    name="surname"
                    value={profileFormData.surname}
                    setValue={handleProfileValueChange}
                    placeholder="Surname"
                    errorMessage="Surname is required"
                    error={profileFormErrors.surname}
                    passCol="col-8"
                    design="flex"
                    maxChars={20}
                    setError={handleProfileErrorChange}
                />
                <TextField
                    label="Email"
                    type="email"
                    name="email"
                    value={profileFormData.email}
                    setValue={handleProfileValueChange}
                    placeholder="Email"
                    errorMessage="Please enter a valid email address"
                    error={profileFormErrors.email}
                    passCol="col-8"
                    design="flex"
                    maxChars={50}
                    setError={handleProfileErrorChange}
                />
                <MobileField
                    label="Mobile"
                    mobileField="mobile"
                    value={profileFormData.mobile}
                    setValue={handleProfileValueChange}
                    placeholder="Mobile"
                    errorMessage="Mobile number is required"
                    error={profileFormErrors.mobile}
                    design="flex"
                    passCol="col-8"
                    maxLength={18}
                    setError={handleProfileErrorChange}
                />
                <Col xs={12}>
                    <Button color="primary" disabled={isUpdateClientDetailsPending} type="submit">{isUpdateClientDetailsPending ? "Loading . . ." : "Save Profile"}</Button>
                </Col>
            </Row>
        </Form>
    );

    return (
        <Container fluid className="d-flex align-items-center justify-content-center" style={{ height: '60vh' }}>
            <Row
                className="d-flex shadow-sm rounded p-4 bg-white"
                style={{
                    width: '100%',
                    maxWidth: '1200px',
                    height: '350px',
                }}
            >
                {/* Form Section for Profile Form */}
                <Col xs={12} className="d-flex flex-column flex-grow-1 p-4">
                    <Card>
                        <CardBody>
                            {renderProfileForm()}
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
}
